import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { createVault, getCreateVaultMutationOptions, getListVaultsQueryKey, type VaultInput } from '@/modules/api/codegen';
import { actions as messagesActions } from '@/modules/messages';
import { useSubscriptionUpgradeModal } from '@/modules/saas-upgrade';
import { isTeamVaultLimitReachedError, isVaultsNotAvailableError } from '@/services/utilities/isApiError';

// Creating is the one vault operation that can answer payment required, so it is the only one routing
// error codes. Feedback goes through the messages module directly, as in `useDeleteVault`.
export const useCreateVault = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { openUpgradeModal } = useSubscriptionUpgradeModal();

    return useMutation({
        mutationKey: getCreateVaultMutationOptions().mutationKey,
        mutationFn: (input: VaultInput) => createVault(input),
        onSuccess: async (_, input) => {
            // The sidebar and the settings table read the same list, so one invalidation refreshes both.
            await queryClient.invalidateQueries({ queryKey: getListVaultsQueryKey() });

            dispatch(
                messagesActions.displaySuccessMessage({
                    message: { id: 'vaults.form.create.success', values: { vault: input.name } },
                    options: { duration: 5 },
                }),
            );
        },
        onError: error => {
            // Reachable because a free user who types the settings URL bypasses the premium link. The
            // upgrade modal carries its own copy, so no message of ours.
            if (isVaultsNotAvailableError(error)) {
                openUpgradeModal();

                return;
            }

            dispatch(
                messagesActions.displayErrorMessage({
                    message: {
                        id: isTeamVaultLimitReachedError(error) ? 'vaults.form.error.limitReached' : 'vaults.form.create.error',
                    },
                    options: { duration: 5 },
                }),
            );
        },
    });
};
